import React, { useState } from 'react';
import Actions from 'flow/actions';
import { List } from 'immutable';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { createTicketForQueueCall } from 'flow/actions/tickets-action';
import { segmentTrack } from 'flow/actions/segment-actions';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { selectQueueMembers } from 'flow/selectors/queue-selectors';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { AppDispatch } from 'flow/types/Redux';
import { State } from 'flow/types/State';
import { Ticket } from 'flow/types/Tickets';
import {
    ActiveCallKey,
    CallRecordKey,
    QueueKey,
    TicketKey,
} from 'flow/types/EntityKeys';
import useAppContext from 'flow/hooks/useAppContext';
import { useModal } from 'flow/contexts/ModalProvider';
import BaseModal from 'flow/components/ui-components/BaseModal';
import PopoverBody from '../OverviewPopover/PopoverBody';
import SelectMembersQueueCallTicket, {
    MembersType,
} from './SelectMembersQueueCallTicket';
import TicketCreateQueueCallInfo from './TicketCreateQueueCallInfo';
import { getTicketIdFromTicketKey } from 'flow/utils/ticket-utils';

interface Props {
    callRecordKey?: CallRecordKey;
    queueKey?: QueueKey;
    callerNumber?: string;
    callTimestamp?: string;
    context?: string;
    activeCallKey?: ActiveCallKey;
}

const TicketCreateViaQueueCallModal = ({
    callRecordKey,
    queueKey,
    callerNumber,
    callTimestamp,
    context,
    activeCallKey,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const queueMembers = useAppSelector((state) =>
        selectQueueMembers(state, queueKey),
    );

    const [ticketCreationInProgress, setTicketCreationInProgress] =
        useState(true);
    const [ticketCreationFailed, setTicketCreationFailed] = useState(false);
    const [ticketCreatedKey, setTicketCreatedKey] = useState<
        TicketKey | undefined
    >(undefined);
    const [membersTypeSelected, setMembersTypeSelected] =
        useState<MembersType>('queuemembers_all');
    const [hasConfirmedCreation, setHasConfirmedCreation] = useState(false);

    const onCreate = () => {
        const getMembersToAdd = () => {
            const queueMemberKeys = List(
                queueMembers?.map((member) => member.get('extensionKey')),
            );
            if (membersTypeSelected === 'queuemembers_all' && queueMemberKeys) {
                return queueMemberKeys?.includes(ownExtensionKey)
                    ? queueMemberKeys
                    : queueMemberKeys?.insert(0, ownExtensionKey);
            }
            return List([ownExtensionKey]);
        };

        dispatch(
            segmentTrack(TICKET_STRINGS.CLICK_CONFIRM_CREATE_TICKET, {
                component: context,
            }),
        );
        setTicketCreationInProgress(true);
        setHasConfirmedCreation(true);

        const lookForQueueCallParent = context === 'call_canvas';
        dispatch(
            createTicketForQueueCall(
                callRecordKey,
                getMembersToAdd(),
                ownExtensionKey,
                lookForQueueCallParent,
            ),
        )
            .then((data: Ticket) => {
                setTicketCreatedKey(data.key);
                setTicketCreationInProgress(false);
                if (activeCallKey) {
                    dispatch(setBagVariableTicketId(activeCallKey, data.key));
                }
            })
            .catch((/*response: Response*/) => {
                setTicketCreationFailed(true);
                setTicketCreationInProgress(false);
            });
    };

    if (!hasConfirmedCreation) {
        return (
            <BaseModal
                open={true}
                header={i18next.t('tickets.ticket.create_ticket')}
                confirmButtonText={i18next.t('tickets.ticket.create_ticket')}
                onContinue={onCreate}
            >
                <div className="flex flex-col gap-2">
                    {context === 'call_canvas' ? null : (
                        <p className="p-2 text-sm">
                            <TicketCreateQueueCallInfo
                                callerNumber={callerNumber}
                                callTimestamp={callTimestamp}
                            />
                        </p>
                    )}
                    <SelectMembersQueueCallTicket
                        context={context}
                        membersTypeSelected={membersTypeSelected}
                        onSelectMembersType={setMembersTypeSelected}
                        queueKey={queueKey}
                    />
                    <div className="rounded-sm bg-gray-100">
                        <p className="p-2 text-xs text-gray-600">
                            {i18next.t('tickets.create.membership_info')}
                        </p>
                    </div>
                </div>
            </BaseModal>
        );
    }

    return (
        <TicketCreated
            creationInProgress={ticketCreationInProgress}
            creationFailed={ticketCreationFailed}
            ticketCreatedKey={ticketCreatedKey}
        />
    );
};

export default TicketCreateViaQueueCallModal;

interface TicketCreatedProps {
    creationInProgress?: boolean;
    creationFailed?: boolean;
    ticketCreatedKey?: TicketKey;
}

const TicketCreated = ({
    creationInProgress,
    creationFailed,
    ticketCreatedKey,
}: TicketCreatedProps) => {
    const { i18next } = useAppContext();
    const { closeModal } = useModal();

    if (creationFailed) {
        return (
            <BaseModal
                open={true}
                header={i18next.t('tickets.create.failed')}
                onSubmit={() => {}}
                confirmButtonText={i18next.t('ok')}
                hideCancelButton={true}
            >
                {i18next.t('tickets.create.failed_error')}
            </BaseModal>
        );
    }

    return (
        <PopoverBody
            open={true}
            onClose={closeModal}
            ticketKey={ticketCreatedKey}
            isLoading={creationInProgress}
            segmentContext="Ticket created modal"
        />
    );
};

TicketCreated.displayName = 'TicketCreated';

const setBagVariableTicketId =
    (activeCallKey: ActiveCallKey, ticketKey: TicketKey) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const ticketId = getTicketIdFromTicketKey(ticketKey);
        dispatch({
            type: Actions.SET_BAG_VARIABLE_TICKET_ID_ACTIVE_CALL,
            activeCallKey,
            ticketId,
            extensionKey: selectOwnExtensionKey(getState()),
        });
    };
